/**
 * Versioned browser-local CSV mapping profiles.
 *
 * A profile is an explicit, reusable import configuration: which source
 * header holds what, plus the converter options around it. It never carries
 * statement data — no rows, no amounts, no output text: the only cell text
 * it keeps is the bounded set of category names the reader explicitly
 * assigned accounts to — so keeping it in `localStorage` (see
 * `src/pages/tools/csv-profile-storage.ts`) does not retain anything the
 * reader pasted beyond their own configuration.
 *
 * Pure and dependency-free apart from sibling `src/lib` validators: no DOM,
 * no storage, no network. Everything crosses the boundary as plain JSON.
 *
 * Contract notes for future readers:
 *
 * - Mappings are stored by source **header name**, not column index. Bank
 *   exports gain or reorder columns between downloads; an index pin would
 *   silently map the wrong column while a name simply stops matching and
 *   resolves to `null`.
 * - The profile carries **no sign rules** (`signColumn`/`debitValues`). Those
 *   are immutable properties of a bank's export shape and stay in the preset
 *   registry (`src/pages/tools/csv-bank-formats.ts`); the profile only keeps
 *   the preset's slug so the UI can rejoin them. A stored sign column would
 *   be both an index pin (see above) and a way to corrupt a verified preset.
 * - Unknown fields are **discarded**, unknown `version`s **rejected**: a
 *   profile written by a newer schema must never half-apply under an old one.
 * - Category configuration is **explicit and bounded**: the category column
 *   by header name (same missing/duplicate handling as every other field)
 *   plus at most {@link MAX_CSV_PROFILE_CATEGORIES} trimmed
 *   value-to-account pairs. Profiles saved before categorization existed
 *   parse with an unmapped column and no assignments, so every row falls
 *   back to `counterAccount`.
 * - Description rules are **explicit and bounded**: at most
 *   {@link MAX_CSV_DESCRIPTION_RULES} ordered literal rules reusing the
 *   description-rule seam's limits, saved only by the explicit profile-save action. Profiles saved
 *   before rules existed carry no `descriptionRules` field and read as "no
 *   rules" — the widget resets its active rules to empty on such a load, so
 *   a rule-free profile never keeps stale rules. Rule patterns are
 *   user-entered configuration that can name merchants: they travel only in
 *   the saved profile, never in analytics payloads or URL parameters.
 * - Size is still one envelope: rules share the per-profile
 *   {@link MAX_CSV_PROFILE_JSON_BYTES} byte bound with everything else. A
 *   realistic rule set (a handful of short patterns) measures in the low
 *   single kilobytes; a maxed-out set (50 rules at every length limit)
 *   exceeds it and is refused at save time with `too-large`, never trimmed
 *   silently — the bound below did not need raising.
 * - Per-row split allocations are **never** profile state. A split
 *   is a one-off edit to one row of one file, keyed by a data-row index and
 *   that row's source identity, and its allocations name amounts: none of it
 *   is reusable configuration. {@link parseConfig} only copies allowlisted
 *   fields and the widget's save snapshot builds `config` field by field, so
 *   neither can carry them; the split key names are listed in
 *   {@link CSV_PROFILE_FORBIDDEN_CONFIG_KEYS} so the round-trip test pins it.
 *   Any future portable profile file inherits this contract: they must exclude
 *   allocations, row indexes and source keys, CSV source text and statement
 *   balances/dates, and must extend this validator rather than define a
 *   second schema that could forget it.
 */

import { isValidAccount, isValidCommodity } from "@/lib/csv-to-beancount";
import type {
  ColumnMapping,
  CsvOutputMode,
} from "@/lib/csv-to-beancount";
import {
  MAX_CSV_DESCRIPTION_RULES,
  MAX_CSV_DESCRIPTION_RULE_ID_LENGTH,
  MAX_CSV_DESCRIPTION_RULE_PATTERN_LENGTH,
  type CsvDescriptionRule,
} from "@/lib/csv-description-rules";
import type { DateOrder } from "@/lib/import-normalize";

/** The only schema version this code reads and writes. */
export const CSV_PROFILE_VERSION = 1;

/** Bounded collection: the storage adapter refuses to write past this. */
export const MAX_CSV_PROFILES = 10;
/** Longest user-visible label, in characters. */
export const MAX_CSV_PROFILE_LABEL_LENGTH = 60;
/** Most header names one profile may carry. */
export const MAX_CSV_PROFILE_HEADERS = 64;
/** Longest single header name, in characters. */
export const MAX_CSV_PROFILE_HEADER_LENGTH = 200;
/**
 * Most category assignments one profile may carry. Mirrors the widget's
 * review bound: categories past the shown list convert through the fallback
 * account, so there is never an assignment worth persisting beyond this.
 */
export const MAX_CSV_PROFILE_CATEGORIES = 50;
/** Longest single category value, in characters. */
export const MAX_CSV_PROFILE_CATEGORY_LENGTH = 200;
/** Longest opaque id / preset slug, in characters. */
const MAX_CSV_PROFILE_ID_LENGTH = 128;
const MAX_CSV_PROFILE_SLUG_LENGTH = 80;
/** Largest serialized profile this code will persist, in UTF-8 bytes. */
export const MAX_CSV_PROFILE_JSON_BYTES = 8192;

/**
 * The converter fields a profile may assign, in widget form order.
 * `category` names the source header holding the category column; the
 * value-to-account assignments live in the config beside it.
 */
export const CSV_PROFILE_FIELDS = [
  "date",
  "description",
  "amount",
  "debit",
  "credit",
  "currency",
  "category",
] as const;

/** One of the seven mappable converter fields. */
export type CsvProfileField = (typeof CSV_PROFILE_FIELDS)[number];

/**
 * Which source header holds what. A value is the header's trimmed text, or
 * `null` when the field is unmapped — never a column index.
 */
export type CsvProfileFieldMapping = Record<CsvProfileField, string | null>;

/** The converter options a profile restores. No statement data, ever. */
export interface CsvProfileConfig {
  sourceAccount: string;
  counterAccount: string;
  /**
   * Explicit category-value → destination-account assignments, keyed by the
   * trimmed cell text. Absent on profiles saved before categorization
   * existed and reads as "no assignments" — every row falls back to
   * `counterAccount`, exactly like an unmapped category column.
   */
  categoryAccounts?: Record<string, string>;
  /**
   * Ordered literal description → account rules (see
   * `@/lib/csv-description-rules`). Array position is the priority: the
   * first enabled matching rule wins, after any explicit category-account
   * assignment. Absent on profiles saved before rules existed and reads as
   * "no rules" — the widget resets its active rules to empty on such a
   * load rather than keeping whatever was there. Disabled rules are
   * preserved as-is so a reload restores the exact editor state.
   */
  descriptionRules?: CsvDescriptionRule[];
  currency: string;
  invertAmounts: boolean;
  dateOrder?: DateOrder;
  outputMode: CsvOutputMode;
}

/**
 * Keys that must never ride along in a profile's `config`. Statement
 * balances and dates, row exclusions and per-row split allocations are
 * session-only widget state; a hand-edited store
 * that smuggles them in is discarded by {@link parseConfig}, never applied.
 */
export const CSV_PROFILE_FORBIDDEN_CONFIG_KEYS = [
  "statement",
  "opening",
  "closing",
  "openingText",
  "closingText",
  "openingBalance",
  "closingBalance",
  "startDate",
  "endDate",
  "startDateText",
  "endDateText",
  "statementStart",
  "statementEnd",
  // Per-row split state: transient, row-keyed and amount-bearing.
  "rowSplits",
  "splits",
  "splitDrafts",
  "allocations",
  "rowIndex",
  "rowIndexes",
  "excludeRowIndexes",
  "sourceKey",
] as const;

/**
 * The complete key vocabulary of a serialized profile, its `config` and one
 * description rule — exactly the fields {@link parseMappingProfile} reads.
 * The portable file (`@/lib/csv-profile-file`) rejects any other key rather
 * than discarding it, so the file and storage schemas cannot drift apart.
 */
export const CSV_PROFILE_KEYS = [
  "version",
  "id",
  "label",
  "presetSlug",
  "headers",
  "mapping",
  "config",
] as const satisfies readonly (keyof CsvMappingProfile)[];
export const CSV_PROFILE_CONFIG_KEYS = [
  "sourceAccount",
  "counterAccount",
  "categoryAccounts",
  "descriptionRules",
  "currency",
  "invertAmounts",
  "dateOrder",
  "outputMode",
] as const satisfies readonly (keyof CsvProfileConfig)[];
export const CSV_DESCRIPTION_RULE_KEYS = [
  "id",
  "enabled",
  "operator",
  "pattern",
  "account",
] as const satisfies readonly (keyof CsvDescriptionRule)[];

const UTF8_ENCODER = new TextEncoder();

/** UTF-8 byte length — the unit every profile size bound is stated in. */
export function utf8ByteLength(text: string): number {
  return UTF8_ENCODER.encode(text).length;
}

/**
 * One persisted mapping profile: identity, the header vocabulary it was
 * saved against, the name-keyed mapping, and the configuration only.
 */
export interface CsvMappingProfile {
  version: typeof CSV_PROFILE_VERSION;
  /** Opaque id from {@link createProfileId} — never shown, never reused. */
  id: string;
  /** Optional user-visible label. Empty string means "unlabeled". */
  label: string;
  /**
   * Which preset the reader was on when saving, or `null` on the generic
   * converter. Rejoined to the registry at apply time for the preset's
   * mapping defaults and immutable sign rules — never trusted for them.
   */
  presetSlug: string | null;
  /** Trimmed header names the mapping below was saved against. */
  headers: string[];
  mapping: CsvProfileFieldMapping;
  config: CsvProfileConfig;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asTrimmedString(value: unknown, maxLength: number): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed.length > maxLength) return null;
  return trimmed;
}

function asSlug(value: unknown): string | null {
  const trimmed = asTrimmedString(value, MAX_CSV_PROFILE_SLUG_LENGTH);
  if (trimmed === null || !/^[a-z0-9-]+$/.test(trimmed)) return null;
  return trimmed;
}

/**
 * An opaque profile id. Prefers `crypto.randomUUID`, falls back to a
 * timestamped random token — read off `globalThis`, never `window`, so this
 * stays callable during SSR.
 */
export function createProfileId(): string {
  const cryptoObject =
    typeof globalThis !== "undefined"
      ? (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
      : undefined;
  try {
    const uuid = cryptoObject?.randomUUID?.();
    if (typeof uuid === "string" && uuid.length > 0) return uuid;
  } catch {
    // Fall through to the Math.random token below.
  }
  return `p-${Date.now().toString(36)}-${Math.floor(Math.random() * 2 ** 48).toString(36)}`;
}

/**
 * Snapshot the widget's index-keyed mapping into header identity against the
 * parsed file's headers. Indexes out of range and blank header names resolve
 * to `null`: a column with no name is not an identity worth keeping.
 */
export function indexMappingToHeaderNames(
  mapping: ColumnMapping,
  headers: readonly string[],
): CsvProfileFieldMapping {
  const names = headers.map((header) => header.trim());
  const at = (index: number | null): string | null => {
    if (index === null || index < 0 || index >= names.length) return null;
    return names[index] === "" ? null : names[index];
  };
  return {
    date: at(mapping.date),
    description: at(mapping.description),
    amount: at(mapping.amount),
    debit: at(mapping.debit),
    credit: at(mapping.credit),
    currency: at(mapping.currency),
    // `?? null`: mappings built before the category field existed carry
    // `undefined`, which reads as unmapped, exactly like `null`.
    category: at(mapping.category ?? null),
  };
}

/**
 * Index trimmed header names to their first column and occurrence count.
 * Shared by resolution and the compatibility check so the two can never
 * disagree about what "present" or "duplicated" means.
 */
function indexHeaderNames(headers: readonly string[] | null): {
  firstByName: Map<string, number>;
  counts: Map<string, number>;
} {
  const firstByName = new Map<string, number>();
  const counts = new Map<string, number>();
  if (headers === null) return { firstByName, counts };
  headers.forEach((header, index) => {
    const name = header.trim();
    if (name === "") return;
    counts.set(name, (counts.get(name) ?? 0) + 1);
    if (!firstByName.has(name)) firstByName.set(name, index);
  });
  return { firstByName, counts };
}

/**
 * Resolve a profile's header names against a newly pasted file's headers.
 * Names that no longer match come back as `null` (unmapped) rather than
 * shifting onto a neighboring column — the failure the compatibility check
 * below reports instead of silently mis-assigning. A name the current file
 * carries more than once is ambiguous, so it also resolves to `null`: picking
 * the first duplicate would be an index guess wearing a name's clothes, and
 * the reader must choose explicitly.
 */
export function resolveProfileMapping(
  profile: CsvMappingProfile,
  headers: readonly string[] | null,
): ColumnMapping {
  const { firstByName, counts } = indexHeaderNames(headers);
  const at = (name: string | null): number | null => {
    if (name === null) return null;
    if ((counts.get(name) ?? 0) > 1) return null;
    return firstByName.get(name) ?? null;
  };
  return {
    date: at(profile.mapping.date),
    description: at(profile.mapping.description),
    amount: at(profile.mapping.amount),
    debit: at(profile.mapping.debit),
    credit: at(profile.mapping.credit),
    currency: at(profile.mapping.currency),
    category: at(profile.mapping.category),
  };
}

/**
 * Header-compatibility verdict for reusing a profile against newly pasted
 * input. Reordered columns are fully compatible — identity is by name, so the
 * same names in another order resolve to the same columns. Anything else
 * needs the reader's eyes before the mapping can be trusted:
 *
 * - `missingHeaders`: mapped names the current file does not carry. The
 *   field resolved to `null`, so conversion blocks ("no amount column
 *   mapped") instead of converting the wrong column.
 * - `duplicateHeaders`: mapped names the current file carries more than
 *   once. Resolution refuses to guess between them (see
 *   {@link resolveProfileMapping}), so the reader picks the column.
 * - `presetMismatch`: the profile was saved under a different preset (or one
 *   side has none). The other export's sign conventions must not silently
 *   ride along — the widget keeps the current route's sign setting.
 */
export interface ProfileCompatibility {
  compatible: boolean;
  missingHeaders: string[];
  duplicateHeaders: string[];
  presetMismatch: boolean;
}

/**
 * Check a profile against the current file before reuse. Pure: the UI half
 * decides what to show for a non-clean verdict, and the widget half decides
 * which settings to hold back.
 */
export function checkProfileCompatibility(
  profile: CsvMappingProfile,
  headers: readonly string[] | null,
  currentPresetSlug: string | null,
): ProfileCompatibility {
  const mapped = Object.values(profile.mapping).filter(
    (name): name is string => name !== null,
  );
  const { firstByName, counts } = indexHeaderNames(headers);
  const present = new Set(firstByName.keys());
  const missingHeaders = mapped.filter((name) => !present.has(name));
  const duplicateHeaders = mapped.filter((name) => (counts.get(name) ?? 0) > 1);
  const presetMismatch =
    (profile.presetSlug ?? null) !== (currentPresetSlug ?? null);
  return {
    compatible:
      missingHeaders.length === 0 &&
      duplicateHeaders.length === 0 &&
      !presetMismatch,
    missingHeaders,
    duplicateHeaders,
    presetMismatch,
  };
}

/**
 * Header names the profile expects but the current file does not carry.
 * Empty for a file the profile applies to cleanly; the UI half
 * ({@link checkProfileCompatibility}) decides what to show for the rest.
 */
export function profileMissingHeaders(
  profile: CsvMappingProfile,
  headers: readonly string[] | null,
): string[] {
  if (headers === null) return [...profile.headers];
  // Defined once: comparing the profile against its own preset can never
  // report a preset mismatch, so this is exactly the missing-headers slice.
  const { missingHeaders } = checkProfileCompatibility(
    profile,
    headers,
    profile.presetSlug,
  );
  return [...new Set(missingHeaders)];
}

function parseFieldMapping(
  raw: unknown,
  headers: string[],
): CsvProfileFieldMapping | null {
  if (!isRecord(raw)) return null;
  const known = new Set(headers);
  const mapping = {} as CsvProfileFieldMapping;
  for (const field of CSV_PROFILE_FIELDS) {
    const value = raw[field];
    if (value === null || value === undefined) {
      mapping[field] = null;
      continue;
    }
    const name = asTrimmedString(value, MAX_CSV_PROFILE_HEADER_LENGTH);
    // A name outside the saved vocabulary cannot have come from this file.
    mapping[field] = name !== null && known.has(name) ? name : null;
  }
  return mapping;
}

/**
 * Validate stored description rules. Absent reads as "no rules" so
 * rule-free version-1 profiles keep loading; a present-but-malformed value
 * rejects the profile (`null`), while unknown per-rule fields are discarded
 * by picking only the known ones.
 *
 * Storage is deliberately stricter than conversion: every stored rule must
 * be structurally valid, disabled ones included — a saved profile must
 * reload exactly what the reader saved. The converter stays lenient (a
 * disabled broken live rule is inert and unblocks conversion); the snapshot
 * side refuses to save such a rule instead of silently dropping it.
 */
function parseDescriptionRules(
  raw: unknown,
): CsvDescriptionRule[] | undefined | null {
  if (raw === undefined) return undefined;
  if (!Array.isArray(raw)) return null;
  if (raw.length > MAX_CSV_DESCRIPTION_RULES) return null;
  const rules: CsvDescriptionRule[] = [];
  const seenIds = new Set<string>();
  for (const entry of raw) {
    if (!isRecord(entry)) return null;
    const id = typeof entry.id === "string" ? entry.id.trim() : "";
    if (id === "" || id.length > MAX_CSV_DESCRIPTION_RULE_ID_LENGTH) {
      return null;
    }
    if (entry.enabled !== undefined && typeof entry.enabled !== "boolean") {
      return null;
    }
    if (entry.operator !== "equals" && entry.operator !== "contains") {
      return null;
    }
    const pattern =
      typeof entry.pattern === "string" ? entry.pattern.trim() : "";
    if (
      pattern === "" ||
      pattern.length > MAX_CSV_DESCRIPTION_RULE_PATTERN_LENGTH
    ) {
      return null;
    }
    const account = asTrimmedString(entry.account, 128);
    if (account === null || !isValidAccount(account)) return null;
    if (seenIds.has(id)) return null;
    seenIds.add(id);
    rules.push({
      id,
      enabled: (entry.enabled ?? true) as boolean,
      operator: entry.operator,
      pattern,
      account,
    });
  }
  return rules;
}

function parseConfig(raw: unknown): CsvProfileConfig | null {
  if (!isRecord(raw)) return null;
  const sourceAccount = asTrimmedString(raw.sourceAccount, 128);
  const counterAccount = asTrimmedString(raw.counterAccount, 128);
  const currency =
    typeof raw.currency === "string" ? raw.currency.trim() : null;
  if (
    sourceAccount === null ||
    !isValidAccount(sourceAccount) ||
    counterAccount === null ||
    !isValidAccount(counterAccount) ||
    currency === null ||
    !isValidCommodity(currency)
  ) {
    return null;
  }
  if (
    raw.invertAmounts !== undefined &&
    typeof raw.invertAmounts !== "boolean"
  ) {
    return null;
  }
  if (
    raw.dateOrder !== undefined &&
    raw.dateOrder !== "mdy" &&
    raw.dateOrder !== "dmy"
  ) {
    return null;
  }
  if (
    raw.outputMode !== undefined &&
    raw.outputMode !== "complete" &&
    raw.outputMode !== "append"
  ) {
    return null;
  }
  const categoryAccounts = parseCategoryAccounts(raw.categoryAccounts);
  if (categoryAccounts === null) return null;
  const descriptionRules = parseDescriptionRules(raw.descriptionRules);
  if (descriptionRules === null) return null;
  // Only the allowlisted fields below are returned. Statement balances and
  // dates listed in {@link CSV_PROFILE_FORBIDDEN_CONFIG_KEYS} are never
  // copied — a hand-edited store that smuggles them in is dropped here.
  return {
    sourceAccount,
    counterAccount,
    // Absent assignments mean "no assignments", as on profiles saved before
    // categorization existed — not an empty object worth serializing back.
    ...(categoryAccounts === undefined ? {} : { categoryAccounts }),
    // Absent rules mean "no rules", as on profiles saved before rules
    // existed — the widget resets its active rules to empty on such a
    // load. An empty array round-trips as-is; the snapshot side omits it.
    ...(descriptionRules === undefined ? {} : { descriptionRules }),
    currency,
    invertAmounts: raw.invertAmounts ?? false,
    // Absent order means "detect from the column", as in the widget.
    ...(raw.dateOrder === undefined ? {} : { dateOrder: raw.dateOrder }),
    outputMode: raw.outputMode ?? "complete",
  };
}

/**
 * Validate the explicit category assignments. Absent reads as "no
 * assignments" so version-1 profiles saved before categorization existed
 * keep loading with every row falling back to the counter account; a
 * present-but-malformed value rejects the profile, while individually bad
 * entries (blank or over-long names, invalid accounts) are dropped and the
 * rest capped — one hand-edited entry must not cost the reader's whole
 * profile.
 */
function parseCategoryAccounts(
  raw: unknown,
): Record<string, string> | undefined | null {
  if (raw === undefined) return undefined;
  if (!isRecord(raw)) return null;
  const assignments: Record<string, string> = {};
  let count = 0;
  for (const [rawKey, rawValue] of Object.entries(raw)) {
    if (count >= MAX_CSV_PROFILE_CATEGORIES) break;
    const key = rawKey.trim();
    if (key === "" || key.length > MAX_CSV_PROFILE_CATEGORY_LENGTH) continue;
    const value = asTrimmedString(rawValue, 128);
    if (value === null || !isValidAccount(value)) continue;
    assignments[key] = value;
    count += 1;
  }
  return assignments;
}

/**
 * Validate one unknown value as a profile. Unknown fields are discarded by
 * construction (only known fields are picked); an unsupported `version`, a
 * bad id, bad headers, or a bad config rejects the whole profile with
 * `null`. In particular there is nowhere here for `rows`, `text`, `output`
 * or any other statement payload to land — extra keys are never read.
 */
export function parseMappingProfile(raw: unknown): CsvMappingProfile | null {
  if (!isRecord(raw)) return null;
  if (raw.version !== CSV_PROFILE_VERSION) return null;
  const id = asTrimmedString(raw.id, MAX_CSV_PROFILE_ID_LENGTH);
  if (id === null) return null;
  if (raw.label !== undefined && raw.label !== null && typeof raw.label !== "string") {
    return null;
  }
  const label =
    typeof raw.label === "string" ? raw.label.trim() : "";
  if (label.length > MAX_CSV_PROFILE_LABEL_LENGTH) return null;
  if (raw.presetSlug !== undefined && raw.presetSlug !== null && asSlug(raw.presetSlug) === null) {
    return null;
  }
  if (!Array.isArray(raw.headers)) return null;
  if (raw.headers.length === 0 || raw.headers.length > MAX_CSV_PROFILE_HEADERS) {
    return null;
  }
  const headers: string[] = [];
  for (const header of raw.headers) {
    const name = asTrimmedString(header, MAX_CSV_PROFILE_HEADER_LENGTH);
    if (name === null) return null;
    if (!headers.includes(name)) headers.push(name);
  }
  const mapping = parseFieldMapping(raw.mapping, headers);
  const config = parseConfig(raw.config);
  if (mapping === null || config === null) return null;
  return {
    version: CSV_PROFILE_VERSION,
    id,
    label,
    presetSlug: raw.presetSlug == null ? null : (raw.presetSlug as string).trim(),
    headers,
    mapping,
    config,
  };
}

/**
 * Validate an unknown persisted value as a profile list. Non-arrays read as
 * empty; malformed entries and unsupported versions are dropped, never
 * thrown. Capped at {@link MAX_CSV_PROFILES} so a hand-edited store cannot
 * grow the collection unboundedly.
 */
export function parseMappingProfileList(raw: unknown): CsvMappingProfile[] {
  if (!isRecord(raw) || !Array.isArray(raw.profiles)) {
    return Array.isArray(raw) ? parseMappingProfileList({ profiles: raw }) : [];
  }
  const profiles: CsvMappingProfile[] = [];
  for (const entry of raw.profiles) {
    if (profiles.length >= MAX_CSV_PROFILES) break;
    const parsed = parseMappingProfile(entry);
    if (parsed !== null && !profiles.some((kept) => kept.id === parsed.id)) {
      profiles.push(parsed);
    }
  }
  return profiles;
}

/** Serialize profiles for storage. Only ever called with validated input. */
export function serializeMappingProfiles(profiles: CsvMappingProfile[]): string {
  return JSON.stringify({ profiles });
}
